/**
 * Registry-owned persistence for the detach handle at
 * `<project>/.skaile/deploy/handle.json` (locked decision #13).
 *
 * The file is a `{ targetId, payload }` envelope: `targetId` selects which
 * `DeployTarget` to look up, `payload` is that target's opaque restore slice.
 * The store never interprets `payload` — it only validates the envelope shape.
 */

import { mkdir, readFile, rm, writeFile } from "node:fs/promises";
import { dirname, join } from "node:path";
import * as z from "zod";

/** Parsed detach-handle envelope. `payload` is the target's opaque restore slice. */
export interface StoredHandle {
  targetId: string;
  payload: unknown;
}

const storedHandleSchema = z.object({
  targetId: z.string().min(1),
  payload: z.unknown(),
});

/** Absolute path to the detach-handle file for a project. */
export function handlePath(projectDir: string): string {
  return join(projectDir, ".skaile", "deploy", "handle.json");
}

/** Write the detach handle, creating `.skaile/deploy/` if missing. */
export async function writeHandle(
  projectDir: string,
  targetId: string,
  payload: unknown,
): Promise<void> {
  const path = handlePath(projectDir);
  await mkdir(dirname(path), { recursive: true });
  const envelope: StoredHandle = { targetId, payload };
  await writeFile(path, `${JSON.stringify(envelope, null, 2)}\n`, "utf8");
}

/** Read + validate the detach handle. Returns null when absent or malformed. */
export async function readHandle(projectDir: string): Promise<StoredHandle | null> {
  let raw: string;
  try {
    raw = await readFile(handlePath(projectDir), "utf8");
  } catch {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const result = storedHandleSchema.safeParse(parsed);
  return result.success ? result.data : null;
}

/** Remove the detach handle. Idempotent — a missing file is not an error. */
export async function clearHandle(projectDir: string): Promise<void> {
  await rm(handlePath(projectDir), { force: true });
}
